import KPICards from '../Components/KPICards';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../Components/Table';
import { Download } from 'lucide-react';
import { Button } from 'retro';
import { kpiExpenseData, expenseTypeSplit, monthlyTrendData, monthlyExpenseTableData } from '../Data/mockData';

export default function Expense() {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

            {/* KPI Cards */}
            <div>
                <h2
                    style={{
                        fontSize: 18,
                        fontWeight: 600,
                        color: '#111827',
                        marginBottom: 16,
                    }}
                >
                    Expense Performance Metrics
                </h2>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(6, 1fr)',
                        gap: 16,
                    }}
                >
                    {kpiExpenseData.map((kpi, index) => (
                        <KPICards
                            key={index}
                            label={kpi.label}
                            value={kpi.value}
                            trend={kpi.trend}
                            bgColor={kpi.bgColor}
                        />
                    ))}
                </div>
            </div>

            {/* Expense Type Split */}
            <div>
                <h2
                    style={{
                        fontSize: 18,
                        fontWeight: 600,
                        color: '#111827',
                        marginBottom: 16,
                    }}
                >
                    Expense Type Breakdown
                </h2>

                <div
                    style={{
                        display: 'flex',
                        gap: 12,
                        flexWrap: 'wrap',       // ✅ allow wrapping on small screens
                        justifyContent: 'flex-start',
                    }}
                >
                    <div
                        style={{
                            background: "linear-gradient(to bottom right, #ecfdf5, #ccfbf1)",
                            borderRadius: 10,
                            border: "1px solid #e5e7eb",
                            padding: 16,
                            width: 400,
                            maxHeight: 200,  // 👈 keeps it compact
                        }}
                    >
                        <h3
                            style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: "#065f46",
                                marginBottom: 12,
                            }}
                        >
                            Self-Paid Metrics
                        </h3>

                        {/* Top row */}
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 12,
                                marginBottom: 12,
                            }}
                        >
                            <div>
                                <div style={{ fontSize: 11, color: "#047857" }}>Total Expenses</div>
                                <div style={{ fontSize: 18, fontWeight: 700, color: "#064e3b" }}>
                                    {expenseTypeSplit.selfPaid.totalExpenses.toLocaleString()}
                                </div>
                            </div>

                            <div>
                                <div style={{ fontSize: 11, color: "#047857" }}>Total Amount</div>
                                <div style={{ fontSize: 18, fontWeight: 700, color: "#064e3b" }}>
                                    {expenseTypeSplit.selfPaid.totalAmount}
                                </div>
                            </div>
                        </div>

                        {/* Bottom row */}
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 12,
                                paddingTop: 8,
                                borderTop: "1px dashed #99f6e4",
                            }}
                        >
                            <div>
                                <div style={{ fontSize: 11, color: "#047857" }}>Paid Expenses</div>
                                <div style={{ fontSize: 16, fontWeight: 600, color: "#064e3b" }}>
                                    {expenseTypeSplit.selfPaid.paidCount.toLocaleString()}
                                </div>
                            </div>

                            <div>
                                <div style={{ fontSize: 11, color: "#047857" }}>Paid Amount</div>
                                <div style={{ fontSize: 16, fontWeight: 600, color: "#064e3b" }}>
                                    {expenseTypeSplit.selfPaid.paidAmount}
                                </div>
                            </div>
                        </div>
                    </div>


                    <div
                        style={{
                            background: "linear-gradient(to bottom right, #eff6ff, #f5f3ff)",
                            borderRadius: 10,
                            border: "1px solid #e5e7eb",
                            padding: 16,
                            flex: "1 1 300px",   // ✅ same responsive behavior
                            maxWidth: 400,
                        }}
                    >
                        <h3
                            style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: "#4338ca",
                                marginBottom: 12,
                            }}
                        >
                            Petty Cash Metrics
                        </h3>

                        {/* Top row */}
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 12,
                                marginBottom: 12,
                            }}
                        >
                            <div>
                                <div style={{ fontSize: 11, color: "#6366f1" }}>Total Expenses</div>
                                <div style={{ fontSize: 18, fontWeight: 700, color: "#1e1b4b" }}>
                                    {expenseTypeSplit.pettyCash.totalExpenses.toLocaleString()}
                                </div>
                            </div>

                            <div>
                                <div style={{ fontSize: 11, color: "#6366f1" }}>Total Amount</div>
                                <div style={{ fontSize: 18, fontWeight: 700, color: "#1e1b4b" }}>
                                    {expenseTypeSplit.pettyCash.totalAmount}
                                </div>
                            </div>
                        </div>

                        {/* Bottom row */}
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 12,
                                paddingTop: 8,
                                borderTop: "1px dashed #c7d2fe",
                            }}
                        >
                            <div>
                                <div style={{ fontSize: 11, color: "#6366f1" }}>Paid Expenses</div>
                                <div style={{ fontSize: 16, fontWeight: 600, color: "#1e1b4b" }}>
                                    {expenseTypeSplit.pettyCash.paidCount.toLocaleString()}
                                </div>
                            </div>

                            <div>
                                <div style={{ fontSize: 11, color: "#6366f1" }}>Paid Amount</div>
                                <div style={{ fontSize: 16, fontWeight: 600, color: "#1e1b4b" }}>
                                    {expenseTypeSplit.pettyCash.paidAmount}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* Monthly Expense Trends */}
            <div
                style={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    border: '1px solid #e5e7eb',
                    padding: 24,
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 16,
                    }}
                >
                    <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827' }}>
                        Monthly Expense Trends
                    </h2>

                    <div style={{ display: 'flex', gap: 8 }}>
                        <button
                            style={{
                                padding: '4px 12px',
                                fontSize: 14,
                                backgroundColor: '#ede9fe',
                                color: '#6d28d9',
                                borderRadius: 6,
                                border: 'none',
                            }}
                        >
                            Amount
                        </button>
                        <button
                            style={{
                                padding: '4px 12px',
                                fontSize: 14,
                                backgroundColor: '#f3f4f6',
                                color: '#374151',
                                borderRadius: 6,
                                border: 'none',
                                cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => (e.target.style.backgroundColor = '#e5e7eb')}
                            onMouseLeave={(e) => (e.target.style.backgroundColor = '#f3f4f6')}
                        >
                            Count
                        </button>
                    </div>
                </div>

                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={monthlyTrendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip
                            formatter={(value) =>
                                `AED ${(value / 1000000).toFixed(2)}M`
                            }
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Line
                            type="monotone"
                            dataKey="amount"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            name="Total Amount"
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Monthly Expense Table */}
            <div
                style={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    border: '1px solid #e5e7eb',
                }}
            >
                <div
                    style={{
                        padding: 24,
                        borderBottom: '1px solid #e5e7eb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827' }}>
                        Monthly Expense Module Data
                    </h2>

                    <Button variant="outline" size="sm">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Download size={16} />
                            Export
                        </div>
                    </Button>
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {[
                                    'Month',
                                    'Total Users',
                                    'Active Users',
                                    'Total Expense Count',
                                    'Total Expense Amount',
                                    'Total Paid Count',
                                    'Total Paid Amount',
                                    'Total Self Paid Count',
                                    'Total Self Paid Amount',
                                ].map((h) => (
                                    <TableHead key={h}>{h}</TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>

                        <TableBody>
                            {monthlyExpenseTableData.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell style={{ fontWeight: 500 }}>{row.month}</TableCell>
                                    <TableCell>{row.totalUsers}</TableCell>
                                    <TableCell>{row.activeUsers}</TableCell>
                                    <TableCell>{row.expenseCount}</TableCell>
                                    <TableCell>{row.expenseAmount}</TableCell>
                                    <TableCell>{row.paidCount}</TableCell>
                                    <TableCell>{row.paidAmount}</TableCell>
                                    <TableCell>{row.selfPaidCount}</TableCell>
                                    <TableCell>{row.selfPaidAmount}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
}
